import { Fragment, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { ROUTE_PATHS } from 'routes/constants';
import { formatDateTime } from 'services/utils';
import Button from 'uikit/Button/Button';
import { WebhookDeliveryDetails } from './WebhookDeliveryDetails';
import './WebhookDeliveries.scss';

const MASS_PAYMENT_EVENT = 'MASS_PAYMENT_UPDATED';

/**
 * Webhook deliveries with expandable attempts and payload.
 * loadDetails(id) is used when the rows come without attempts/payload (customer history);
 * without it the row itself is shown as details (transaction tab).
 */
export const WebhookDeliveriesTable = ({ deliveries, isShowTransaction, loadDetails }) => {
  const i18n = useContext(i18nContext);
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});
  const [failedIds, setFailedIds] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  // a reloaded list may have new attempts: drop cached details (expanded rows refetch below)
  useEffect(() => {
    setDetails({});
    setFailedIds({});
  }, [deliveries]);

  const fetchDetails = async (deliveryId) => {
    setLoadingId(deliveryId);
    setFailedIds((prev) => ({ ...prev, [deliveryId]: false }));
    try {
      const loaded = await loadDetails(deliveryId);
      setDetails((prev) => ({ ...prev, [deliveryId]: loaded }));
    } catch (err) {
      // api() already shows the error toast
      setFailedIds((prev) => ({ ...prev, [deliveryId]: true }));
    } finally {
      setLoadingId(null);
    }
  };

  // expanded row of a reloaded list: load its fresh details
  useEffect(() => {
    if (loadDetails && expandedId && !details[expandedId] && !failedIds[expandedId] && loadingId !== expandedId) {
      fetchDetails(expandedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, expandedId]);

  const columns = [
    'createdAt',
    'webhookUrl',
    'direction',
    ...(isShowTransaction ? ['transactionNumber'] : []),
    'status',
    'attemptCount',
    'lastAttemptAt',
    'lastHttpStatus',
    'actions'
  ];

  const handleToggle = (delivery) => {
    if (expandedId === delivery.id) {
      setExpandedId(null);
      return;
    }
    setFailedIds((prev) => ({ ...prev, [delivery.id]: false }));
    setExpandedId(delivery.id);
  };

  const renderTransaction = (delivery) => {
    if (!delivery.transactionNumber) return '';
    if (delivery.eventType === MASS_PAYMENT_EVENT) {
      return (
        <span className={'webhook-delivery-mass-payment'}>
          {i18n.getMessage('webhookDeliveries.massPayment')} {delivery.transactionNumber}
        </span>
      );
    }
    return (
      <NavLink
        className={'webhook-delivery-transaction-link'}
        to={`${ROUTE_PATHS.TRANSACTIONS}/${delivery.transactionNumber}?tab=webhooks`}
      >
        {delivery.transactionNumber}
      </NavLink>
    );
  };

  const renderLastHttpStatus = (delivery) => {
    if (delivery.lastHttpStatus !== null && delivery.lastHttpStatus !== undefined) {
      return delivery.lastHttpStatus;
    }
    // no attempts recorded (old delivery) or no response received
    return delivery.lastAttemptAt ? i18n.getMessage('webhookDeliveries.noResponse') : '—';
  };

  if (!deliveries?.length) {
    return <p className={'webhook-deliveries-empty'}>{i18n.getMessage('webhookDeliveries.empty')}</p>;
  }

  return (
    <Table responsive className={'webhook-deliveries-table'}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column} className={'webhook-deliveries-head'}>
              {i18n.getMessage(`webhookDeliveries.header.${column}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {deliveries.map((delivery) => {
          const isExpanded = expandedId === delivery.id;
          const detailsData = loadDetails ? details[delivery.id] : delivery;
          return (
            <Fragment key={delivery.id}>
              <tr className={`webhook-deliveries-row ${isExpanded ? 'expanded' : ''}`}>
                <td className={'webhook-deliveries-cell'}>{formatDateTime(delivery.createdAt)}</td>
                <td className={'webhook-deliveries-cell webhook-deliveries-url'}>{delivery.webhookUrl}</td>
                <td className={'webhook-deliveries-cell'}>
                  {delivery.webhookDirection ? (
                    <span className={`webhook-direction-badge ${delivery.webhookDirection.toLowerCase()}`}>
                      {i18n.getMessage(`webhookTab.direction.${delivery.webhookDirection}`)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                {isShowTransaction && <td className={'webhook-deliveries-cell'}>{renderTransaction(delivery)}</td>}
                <td className={'webhook-deliveries-cell'}>
                  <span className={`webhook-delivery-status ${delivery.status?.toLowerCase() || ''}`}>
                    {i18n.getMessage(`webhookDeliveries.status.${delivery.status}`)}
                  </span>
                  {delivery.test && (
                    <span className={'webhook-delivery-test-badge'}>{i18n.getMessage('webhookDeliveries.test')}</span>
                  )}
                </td>
                <td className={'webhook-deliveries-cell'}>{delivery.attemptCount}</td>
                <td className={'webhook-deliveries-cell'}>
                  {/* old deliveries have no attempts, processedAt is the time of their last one */}
                  {formatDateTime(delivery.lastAttemptAt || delivery.processedAt) || '—'}
                </td>
                <td className={'webhook-deliveries-cell'}>{renderLastHttpStatus(delivery)}</td>
                <td className={'webhook-deliveries-cell'}>
                  <Button
                    className={'webhook-deliveries-button'}
                    roleType={'button'}
                    size={'small'}
                    type={isExpanded ? 'outline' : 'primary'}
                    onClick={() => handleToggle(delivery)}
                  >
                    {i18n.getMessage(isExpanded ? 'webhookDeliveries.button.hide' : 'webhookDeliveries.button.details')}
                  </Button>
                </td>
              </tr>
              {isExpanded && (
                <tr className={'webhook-deliveries-details-row'}>
                  <td colSpan={columns.length}>
                    {loadDetails && failedIds[delivery.id] ? (
                      <p className={'webhook-deliveries-error'}>{i18n.getMessage('webhookDeliveries.loadError')}</p>
                    ) : loadingId === delivery.id || !detailsData ? (
                      <Loader className={'webhook-deliveries-loader'} />
                    ) : (
                      <WebhookDeliveryDetails delivery={detailsData} />
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};

WebhookDeliveriesTable.propTypes = {
  deliveries: PropTypes.array,
  isShowTransaction: PropTypes.bool,
  loadDetails: PropTypes.func
};
